import React, { useEffect, useState, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  useColorModeValue,
  Icon,
  Divider,
  Badge,
  Link,
  useDisclosure,
  Flex,
  Spinner,
  Tooltip,
  useBreakpointValue,
  IconButton,
  Card,
  CardBody,
  Heading,
} from '@chakra-ui/react';
import {
  FiDownload,
  FiExternalLink,
  FiCheck,
  FiX,
  FiSmartphone,
  FiGlobe,
  FiCopy,
  FiUser,
  FiShield,
  FiZap,
  FiSettings,
} from 'react-icons/fi';
import { FaWallet } from 'react-icons/fa';
import { useAccount, useConnect, useDisconnect, useSignMessage, useNetwork, useSwitchNetwork } from 'wagmi';
import { MetaMaskConnector } from 'wagmi/connectors/metaMask';
import { MetaMaskSDK } from '@metamask/sdk';
import { isMobile, isIOS } from 'react-device-detect';

// 辅助函数：检测是否在原生App中
const isNativeApp = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  // 检查是否是真正的原生App环境
  return userAgent.includes('wv') || // Android WebView
         (userAgent.includes('mobile') && userAgent.includes('safari') && !userAgent.includes('chrome') && !userAgent.includes('firefox') && !userAgent.includes('edg')); // iOS WebView
};

// 辅助函数：检测是否在浏览器环境中
const isInBrowser = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  // 检查是否是浏览器环境（包括移动浏览器）
  return userAgent.includes('chrome') || 
         userAgent.includes('firefox') || 
         userAgent.includes('safari') || 
         userAgent.includes('edge') ||
         userAgent.includes('opera');
};

// 声明window.ethereum类型
declare global {
  interface Window {
    ethereum?: {
      isMetaMask?: boolean;
      isTrust?: boolean;
      isTokenPocket?: boolean;
      providers?: any[];
      _state?: any;
      networkVersion?: string;
      selectedAddress?: string;
      request: (args: any) => Promise<any>;
      on: (event: string, callback: any) => void;
      removeListener: (event: string, callback: any) => void;
    };
  }
}

interface MetaMaskConnectProps {
  isOpen: boolean;
  onClose: () => void;
}

const MetaMaskConnect: React.FC<MetaMaskConnectProps> = ({ isOpen, onClose }) => {
  const { address, isConnected } = useAccount();
  const { connect, connectors, isLoading } = useConnect();
  const { disconnect } = useDisconnect();
  const { chain } = useNetwork();
  const { switchNetwork } = useSwitchNetwork();
  const { signMessage } = useSignMessage();
  const toast = useToast();
  const [sdk, setSdk] = useState<MetaMaskSDK | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [copied, setCopied] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const isMobileView = useBreakpointValue({ base: true, md: false });

  // 初始化 MetaMask SDK
  useEffect(() => {
    const initSDK = async () => {
      try {
        // 在浏览器环境中且有MetaMask扩展时，不初始化SDK
        const hasMetaMaskExtension = typeof window.ethereum !== 'undefined' && window.ethereum.isMetaMask;
        const shouldUseSDK = !hasMetaMaskExtension || !isInBrowser();
        
        let metaMaskSDK: MetaMaskSDK | null = null;
        
        if (shouldUseSDK) {
          metaMaskSDK = new MetaMaskSDK({
            dappMetadata: { 
              name: 'DOR Digital Open Reward Dollar',
              url: window.location.origin,
            },
            extensionOnly: isMobile ? false : true, // 桌面端只使用扩展，移动端支持App
            checkInstallationImmediately: true,
          });
          
          setSdk(metaMaskSDK);
        } else {
          setSdk(null);
        }
        
        // 检测安装状态 - 使用多种方法检测
        let installed = false;
        
        // 方法1: 检查window.ethereum (浏览器扩展)
        if (typeof window.ethereum !== 'undefined') {
          // 检查是否是MetaMask
          if (window.ethereum.isMetaMask) {
            installed = true;
          }
          
          // 检查其他可能的MetaMask标识
          if (window.ethereum.providers) {
            const metamaskProvider = window.ethereum.providers.find(
              (provider: any) => provider.isMetaMask
            );
            if (metamaskProvider) {
              installed = true;
            }
          }
        }
        
        // 方法2: 检查SDK的provider（只有在使用SDK时才检查）
        if (!installed && shouldUseSDK && metaMaskSDK) {
          try {
            const provider = metaMaskSDK.getProvider();
            if (provider) {
              installed = true;
            }
          } catch (error) {
            // SDK provider不可用
          }
        }
        
        // 方法3: 尝试直接检测MetaMask扩展
        if (!installed && isInBrowser()) {
          try {
            // 尝试检测MetaMask扩展的特定属性
            if (window.ethereum && (
              window.ethereum.isMetaMask ||
              window.ethereum._state ||
              window.ethereum.networkVersion ||
              window.ethereum.selectedAddress
            )) {
              installed = true;
            }
          } catch (error) {
            // 扩展检测错误
          }
        }
        
        // 方法4: 移动端特殊检测
        if (isMobile && !installed) {
          // 在移动端，我们假设用户可能需要下载App
          // 但不会阻止连接尝试，因为SDK可能会处理deep link
        }
        
        setIsInstalled(installed);
        
      } catch (error) {
        // Failed to initialize MetaMask SDK
        setIsInstalled(false);
      }
    };

    initSDK();
  }, [isMobile]);

  // 处理钱包连接
  const handleConnect = useCallback(async () => {
    setIsConnecting(true);
    try {
      // 检查是否在浏览器环境中且有MetaMask扩展
      const hasMetaMaskExtension = typeof window.ethereum !== 'undefined' && window.ethereum.isMetaMask;
      
      let accounts;
      
      if (hasMetaMaskExtension && isInBrowser() && window.ethereum) {
        // 在浏览器环境中，直接使用window.ethereum，完全绕过SDK
        try {
          // 直接请求账户，不使用SDK
          accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        } catch (error) {
          // Direct extension connection failed
          throw error;
        }
      } else {
        // 只有在没有扩展的情况下才使用SDK
        if (!sdk) {
          throw new Error('MetaMask SDK not initialized');
        }
        accounts = await sdk.connect();
      }
      
      if (accounts && accounts.length > 0) {
        
        // 签名验证
        setIsSigning(true);
        try {
          const message = `欢迎使用DOR稳定币！\n\n地址: ${accounts[0]}\n时间: ${new Date().toISOString()}\n\n请签名以验证您的身份。`;
          
          // 直接使用MetaMask扩展进行签名，而不是Wagmi
          let signature;
          if (window.ethereum) {
            signature = await window.ethereum.request({
              method: 'personal_sign',
              params: [message, accounts[0]]
            });
          } else {
            // 回退到Wagmi签名
            signature = await signMessage({ message });
          }
          
          // 连接成功后，通过Wagmi connector更新状态
          if (hasMetaMaskExtension && isInBrowser()) {
            // 找到MetaMask connector并连接
            const metaMaskConnector = connectors.find(c => c.id === 'metaMask');
            if (metaMaskConnector) {
              await connect({ connector: metaMaskConnector });
            }
          }
          
          toast({
            title: '连接成功',
            description: 'MetaMask 钱包已成功连接并验证',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        } catch (signError) {
          // 签名失败
          toast({
            title: '连接成功',
            description: 'MetaMask 钱包已连接，但签名验证失败',
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
        } finally {
          setIsSigning(false);
        }
        
        onClose();
      } else {
        throw new Error('未获取到账户地址');
      }
    } catch (error) {
      // 连接错误
      
      // 处理不同类型的错误
      if (error instanceof Error) {
        const errorMessage = error.message;
        
        // 检查是否是deep link错误
        if (errorMessage.includes('scheme does not have a registered handler') || 
            errorMessage.includes('Failed to launch') ||
            errorMessage.includes('metamask://')) {
          
          if (isMobile) {
            toast({
              title: 'MetaMask App 未安装',
              description: '请先下载并安装 MetaMask App，然后重试',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          } else {
            toast({
              title: 'MetaMask 扩展未安装',
              description: '请先安装 MetaMask 浏览器扩展，然后重试',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          }
        } else if (errorMessage.includes('User rejected')) {
          toast({
            title: '连接被取消',
            description: '用户取消了连接请求',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
        } else if (errorMessage.includes('network')) {
          toast({
            title: '网络错误',
            description: '请检查网络连接并重试',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: '连接失败',
            description: errorMessage,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        // 如果是移动端且未安装，显示下载提示
        if (isMobile && !isInstalled) {
          toast({
            title: 'MetaMask 未安装',
            description: '请先下载并安装 MetaMask App',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: '连接失败',
            description: '连接过程中出现错误，请重试',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } finally {
      setIsConnecting(false);
    }
  }, [sdk, signMessage, isMobile, isInstalled, toast, onClose, connectors, connect]);

  // 处理断开连接
  const handleDisconnect = useCallback(() => {
    if (sdk) {
      sdk.disconnect();
    }
    disconnect();
    toast({
      title: '已断开连接',
      description: 'MetaMask 钱包连接已断开',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
    onClose();
  }, [sdk, disconnect, toast, onClose]);

  // 处理网络切换
  const handleSwitchNetwork = useCallback(() => {
    if (switchNetwork) {
      switchNetwork(97); // BSC测试网
      toast({
        title: '网络切换',
        description: '正在切换到BSC测试网',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [switchNetwork, toast]);

  // 复制地址
  const copyAddress = useCallback(() => {
    if (address) {
      navigator.clipboard.writeText(address);
      setCopied(true);
      toast({
        title: '地址已复制',
        description: '钱包地址已复制到剪贴板',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      setTimeout(() => setCopied(false), 2000);
    }
  }, [address, toast]);

  // 格式化地址显示
  const formatAddress = useCallback((addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  }, []);

  // 获取下载链接
  const getDownloadUrl = useCallback(() => {
    if (isIOS) {
      return 'https://apps.apple.com/app/metamask/id1438144202';
    } else {
      return 'https://play.google.com/store/apps/details?id=io.metamask';
    }
  }, []);

  // 如果已连接，显示连接状态
  if (isConnected && address) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="sm">
        <ModalOverlay />
        <ModalContent bg={bgColor} border="1px" borderColor={borderColor}>
          <ModalHeader textAlign="center">
            <VStack spacing={2}>
                              <FaWallet color="#F6851B" size={24} />
              <Text fontSize="lg" fontWeight="bold">MetaMask 已连接</Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            <VStack spacing={4}>
              <Card bg={cardBg} w="full">
                <CardBody>
                  <VStack spacing={3}>
                    <HStack>
                      <FiUser color="blue.500" />
                      <Text fontWeight="bold">连接地址</Text>
                    </HStack>
                    <HStack>
                      <Text
                        fontFamily="mono"
                        fontSize="md"
                        fontWeight="bold"
                        color="blue.700"
                        _dark={{ color: 'blue.200' }}
                      >
                        {formatAddress(address)}
                      </Text>
                      <IconButton
                        size="sm"
                        variant="ghost"
                        icon={copied ? <FiCheck /> : <FiCopy />}
                        onClick={copyAddress}
                        aria-label="复制地址"
                      />
                    </HStack>
                    <Badge colorScheme="green" size="sm">
                      {chain?.name || 'BSC测试网'}
                    </Badge>
                  </VStack>
                </CardBody>
              </Card>
              
              <Alert status="info" size="sm">
                <AlertIcon />
                <Box>
                  <AlertTitle fontSize="sm">连接状态</AlertTitle>
                  <AlertDescription fontSize="xs">
                    您的 MetaMask 钱包已成功连接到DOR应用
                  </AlertDescription>
                </Box>
              </Alert>

              <VStack spacing={2} w="full">
                <Button
                  size="sm"
                  variant="outline"
                  w="full"
                  leftIcon={<FiGlobe />}
                  onClick={handleSwitchNetwork}
                >
                  切换到BSC测试网
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  w="full"
                  leftIcon={<FiSettings />}
                  onClick={() => {
                    toast({
                      title: '功能开发中',
                      description: '钱包设置功能即将上线',
                      status: 'info',
                      duration: 3000,
                      isClosable: true,
                    });
                  }}
                >
                  钱包设置
                </Button>
              </VStack>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="red"
              variant="outline"
              onClick={handleDisconnect}
              w="full"
                              leftIcon={<FiX />}
            >
              断开连接
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg={bgColor} border="1px" borderColor={borderColor}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box as="img" src="https://cdn.iconscout.com/icon/free/png-256/metamask-2728406-2261817.png" boxSize={6} />
            <Text>连接 MetaMask 钱包</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4}>
            <Card bg={cardBg} w="full" border="2px" borderColor="#F6851B">
              <CardBody>
                <VStack spacing={4}>
                  <HStack spacing={4} w="full">
                    <Box
                      w="80px"
                      h="80px"
                      borderRadius="xl"
                      overflow="hidden"
                      border="3px"
                      borderColor="#F6851B"
                      bg="#F6851B"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <FaWallet color="white" size={48} />
                    </Box>
                    
                    <VStack align="flex-start" spacing={2} flex={1}>
                      <Heading size="md" color="#F6851B">
                        MetaMask
                      </Heading>
                      <Text fontSize="sm" color="gray.500">
                        最受欢迎的Web3钱包
                      </Text>
                      <HStack spacing={2}>
                        <Badge size="sm" colorScheme="blue">多链支持</Badge>
                        <Badge size="sm" colorScheme="blue">NFT管理</Badge>
                        <Badge size="sm" colorScheme="blue">DeFi集成</Badge>
                      </HStack>
                    </VStack>
                  </HStack>

                  <Divider />

                  {!isInstalled ? (
                    <VStack spacing={3} w="full">
                      <Alert status="warning" size="sm">
                        <AlertIcon />
                        <Box>
                          <AlertTitle fontSize="sm">MetaMask 未安装</AlertTitle>
                          <AlertDescription fontSize="xs">
                            请先下载并安装 MetaMask {isMobile ? 'App' : '浏览器扩展'}
                          </AlertDescription>
                        </Box>
                      </Alert>
                      
                      <VStack spacing={2} w="full">
                        <Button
                          colorScheme="orange"
                          w="full"
                          leftIcon={
                            <Box as="img" src="https://cdn.iconscout.com/icon/free/png-256/metamask-2728406-2261817.png" boxSize={4} />
                          }
                          onClick={() => window.open(getDownloadUrl(), '_blank')}
                        >
                          下载 MetaMask {isMobile ? 'App' : '扩展'}
                        </Button>
                        
                        <Button
                          variant="outline"
                          w="full"
                          leftIcon={<FiExternalLink />}
                          onClick={() => window.open('https://metamask.io/', '_blank')}
                        >
                          访问官网
                        </Button>
                      </VStack>
                    </VStack>
                  ) : (
                    <VStack spacing={3} w="full">
                      <Alert status="success" size="sm">
                        <AlertIcon />
                        <Box>
                          <AlertTitle fontSize="sm">MetaMask 已安装</AlertTitle>
                          <AlertDescription fontSize="xs">
                            点击下方按钮连接到 MetaMask
                          </AlertDescription>
                        </Box>
                      </Alert>
                      
                      <Button
                        colorScheme="orange"
                        w="full"
                        size="lg"
                        onClick={handleConnect}
                        isLoading={isConnecting || isLoading || isSigning}
                        loadingText={isSigning ? "签名验证中..." : "连接中..."}
                        leftIcon={
                          <Box as="img" src="https://cdn.iconscout.com/icon/free/png-256/metamask-2728406-2261817.png" boxSize={5} />
                        }
                      >
                        连接 MetaMask
                      </Button>
                    </VStack>
                  )}

                  {/* 移动端特殊提示 */}
                  {isMobile && (
                    <Alert status="info" size="sm">
                      <AlertIcon />
                      <Box>
                        <AlertTitle fontSize="sm">移动端提示</AlertTitle>
                        <AlertDescription fontSize="xs">
                          {isInstalled 
                            ? '点击连接后会自动打开 MetaMask App 进行授权'
                            : '如果已安装 MetaMask App，仍可尝试连接，系统会自动打开 App'
                          }
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}
                </VStack>
              </CardBody>
            </Card>

            {/* 安全提示 */}
            <Alert status="info" size="sm">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">安全提示</AlertTitle>
                <AlertDescription fontSize="xs">
                  请确保从官方渠道下载 MetaMask，并妥善保管您的私钥。连接后需要进行签名验证。
                </AlertDescription>
              </Box>
            </Alert>

            {/* 设备信息 */}
            <Alert status="info" size="sm">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">设备信息</AlertTitle>
                <AlertDescription fontSize="xs">
                  当前设备: {isMobile ? '移动端' : '桌面端'} 
                  {isMobile && (isIOS ? ' (iOS)' : ' (Android)')}
                </AlertDescription>
              </Box>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose} w="full">
            取消
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MetaMaskConnect; 